import React from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import RequireRoles from 'components/common/requireRole';
import { getNavigationTreeLineColor } from 'components/utils';
import { pathMatchesCurrentRoute, pathMatchesSettingsTabSubItem, pathQueryPrefixMatches } from './settingsHomeRouting';

export interface SectionSubItem {
  title: string;
  link: string;
  testId?: string;
  roles?: string[];
}

export interface SectionItem {
  title?: string;
  link?: string;
  content?: React.ReactNode;
  roles?: string[];
  icon: React.ReactNode;
  testId?: string;
  /** When set with subItems, renders an expandable row (main nav Outreach-style). */
  submenuKey?: string;
  subItems?: SectionSubItem[];
}

export type SettingsSectionProps = {
  title?: string;
  items: SectionItem[];
  isDanger?: boolean;
};

const SettingsSection = ({ title, items, isDanger = false }: SettingsSectionProps) => {
  const router = useRouter();

  return (
    <Box id={title?.toLowerCase().replace(' ', '-') || ''}>
      {title && (
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mt: isDanger ? 3 : 2,
            mb: 0.5,
            px: 1,
            color: isDanger ? 'warning.main' : 'secondary.contrastText',
            opacity: isDanger ? 1 : 0.7,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            lineHeight: 1.2,
            textTransform: 'capitalize',
          }}
        >
          {title}
        </Typography>
      )}

      <List
        sx={theme => ({
          py: 0,
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        })}
      >
        {items.map(item => {
          const hasSubmenu = Boolean(item.subItems?.length && item.submenuKey);
          const isSubmenuOpen = hasSubmenu;
          const hasActiveSubItem =
            hasSubmenu && item.subItems!.some(sub => pathMatchesSettingsTabSubItem(router.asPath, sub.link));
          const parentSubmenuContextActive = Boolean(
            hasSubmenu &&
            item.link &&
            (pathQueryPrefixMatches(router.asPath, item.link) ||
              item.subItems!.some(sub => pathQueryPrefixMatches(router.asPath, sub.link))),
          );
          const active =
            item.link && !hasSubmenu
              ? pathMatchesCurrentRoute(router.asPath, item.link)
              : Boolean(parentSubmenuContextActive && (isSubmenuOpen || hasActiveSubItem));

          const rowSx = (theme: Theme) => ({
            padding: '5px 8px',
            mt: 0.5,
            borderRadius: '4px',
            transition: 'background-color 0.3s ease',
            ':hover': {
              backgroundColor: theme.palette.primary.main,
            },
            ...(active
              ? {
                  backgroundColor: theme.palette.primary.main,
                }
              : {}),
          });

          const iconAndText = (
            <>
              <ListItemIcon
                sx={{
                  color: 'secondary.contrastText',
                  minWidth: 22,
                  opacity: 0.6,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                sx={{
                  color: '#e7e0e0',
                  margin: 0,
                  fontSize: '13px',
                  fontWeight: 600,
                  '& .MuiListItemText-primary': {
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
                secondary={
                  item.content ? (
                    <Box component="span" sx={{ display: 'block', mt: 1, color: 'rgba(231, 224, 224, 0.75)' }}>
                      {item.content}
                    </Box>
                  ) : undefined
                }
              />
            </>
          );

          const plainRow =
            item.link && !hasSubmenu ? (
              <ListItemButton component={NextLink} href={item.link} scroll={false} data-testid={item.testId} sx={rowSx}>
                {iconAndText}
              </ListItemButton>
            ) : !hasSubmenu ? (
              <ListItem sx={rowSx}>{iconAndText}</ListItem>
            ) : null;

          const submenuRow =
            hasSubmenu && item.link && item.submenuKey ? (
              <Box>
                <ListItem
                  disablePadding
                  sx={theme => ({
                    mt: 0.5,
                    borderRadius: '4px',
                    transition: 'background-color 0.3s ease',
                    overflow: 'hidden',
                    ':hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                    ...(parentSubmenuContextActive && (isSubmenuOpen || hasActiveSubItem)
                      ? {
                          backgroundColor: theme.palette.primary.main,
                        }
                      : {}),
                  })}
                >
                  <ListItemButton
                    component={NextLink}
                    href={item.link}
                    scroll={false}
                    data-testid={item.testId}
                    sx={{
                      py: 0.35,
                      px: 1,
                      borderRadius: '4px',
                      color: 'inherit',
                    }}
                  >
                    {iconAndText}
                  </ListItemButton>
                </ListItem>
                <Box
                  sx={theme => ({
                    pl: `calc(${theme.spacing(1.5)} + 5px)`,
                    pr: theme.spacing(1),
                    boxSizing: 'border-box',
                    mt: 0.75,
                    mb: 0,
                    pb: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    overflowX: 'hidden',
                    overflowY: 'visible',
                  })}
                >
                  {item.subItems!.map((sub, idx) => {
                    const isLast = idx === item.subItems!.length - 1;
                    const subActive = pathMatchesSettingsTabSubItem(router.asPath, sub.link);
                    const subItem = (
                      <NextLink
                        href={sub.link}
                        scroll={false}
                        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                        data-testid={sub.testId}
                      >
                        <Box sx={{ position: 'relative', pl: 3 }}>
                          {!isLast && (
                            <Box
                              sx={theme => ({
                                position: 'absolute',
                                left: 8,
                                top: 0,
                                bottom: '-4px',
                                width: 0,
                                borderLeft: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                                pointerEvents: 'none',
                              })}
                            />
                          )}
                          <Box
                            sx={theme => ({
                              position: 'absolute',
                              left: 8,
                              top: 0,
                              width: 16,
                              height: '50%',
                              borderLeft: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                              borderBottom: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                              borderBottomLeftRadius: '12px',
                              pointerEvents: 'none',
                            })}
                          />
                          <ListItem
                            dense
                            disablePadding
                            sx={theme => ({
                              padding: '4px 8px',
                              borderRadius: '4px',
                              minHeight: 26,
                              transition: 'background-color 0.2s ease',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                              },
                              ...(subActive
                                ? {
                                    backgroundColor: theme.palette.primary.main,
                                    '& .MuiListItemText-primary': {
                                      color: theme.palette.primary.contrastText,
                                    },
                                  }
                                : {}),
                            })}
                          >
                            <ListItemText
                              sx={theme => ({
                                color: theme.palette.secondary.contrastText,
                                margin: 0,
                                opacity: 0.8,
                                '& .MuiListItemText-primary': {
                                  fontSize: '12px',
                                  lineHeight: 1.3,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              })}
                            >
                              {sub.title}
                            </ListItemText>
                          </ListItem>
                        </Box>
                      </NextLink>
                    );

                    return (
                      <React.Fragment key={sub.link}>
                        {sub.roles ? <RequireRoles roles={sub.roles}>{subItem}</RequireRoles> : subItem}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            ) : null;

          const finalContent = submenuRow ?? plainRow;

          return (
            <React.Fragment key={item.title}>
              {item.roles ? <RequireRoles roles={item.roles}>{finalContent}</RequireRoles> : finalContent}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default SettingsSection;
